import { Slot } from '@radix-ui/react-slot';
import { Loader } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

import { buttonVariants, buttonSpinnerVariants } from './Button.styles';

import type { ButtonProps } from './Button.types';

/**
 * Button component
 *
 * Interactive button component with multiple appearance variants and scale options.
 * Based on Figma Design System Nabat 2.0 - Buttons
 *
 * @example
 * // Basic button
 * <Button>Click me</Button>
 *
 * @example
 * // Brand button with icon
 * <Button kind="brand" iconStart={<Plus />}>
 *   Add Item
 * </Button>
 *
 * @example
 * // Loading state
 * <Button loading>Processing...</Button>
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      appearance,
      kind,
      scale,
      width,
      iconStart,
      iconEnd,
      loading = false,
      disabled,
      children,
      asChild = false,
      ...props
    },
    ref
  ) => {
    // Extract scale for spinner variant
    const spinnerScale = scale || 'm';

    const decorate = (label: React.ReactNode) => (
      <>
        {loading && (
          <Loader
            className={cn(buttonSpinnerVariants({ scale: spinnerScale }))}
            aria-label="Loading"
          />
        )}
        {!loading && iconStart && <span className="shrink-0">{iconStart}</span>}
        {label && <span className="whitespace-nowrap">{label}</span>}
        {!loading && iconEnd && <span className="shrink-0">{iconEnd}</span>}
      </>
    );

    const sharedProps = {
      className: cn(buttonVariants({ appearance, kind, scale, width, className })),
      'aria-busy': loading,
      ...props,
    };

    // `Slot` takes exactly one child, so the decorations go inside the caller's
    // element. `disabled` is dropped: not valid on the anchors this renders.
    if (asChild && React.isValidElement<{ children?: React.ReactNode }>(children)) {
      return (
        <Slot ref={ref} {...sharedProps}>
          {React.cloneElement(children, undefined, decorate(children.props.children))}
        </Slot>
      );
    }

    return (
      <button ref={ref} disabled={disabled || loading} {...sharedProps}>
        {decorate(children)}
      </button>
    );
  }
);

Button.displayName = 'Button';
